import { useEffect, useRef, useState } from "react";
import { api, idr } from "../../lib/api";
import { Sparkles, Send, X, Loader2, Plus, CheckCheck, Info } from "lucide-react";

/**
 * Panel chat AI untuk mengurai teks bebas jadi baris barang nota.
 *
 * Riwayat sengaja hanya hidup di state komponen (tidak dipersist) — sesi urai
 * ini pendek dan terikat satu nota; menyimpannya justru membingungkan waktu
 * nota berikutnya dibuka dengan sisa chat nota lama.
 *
 * Props:
 *  - mode: "sales" | "akuisisi" — menentukan bentuk item & izin di backend
 *  - onApply(items): dipanggil dengan array item hasil urai untuk diisikan ke form
 *  - onClose(): tutup panel
 */
const CONTOH = {
  sales: 'Contoh: "iphone 13 pro 128 second 8.5jt imei 351234567890123, casing spigen 2x 150rb"',
  akuisisi: 'Contoh: "beli iphone 12 64gb bekas 4.2jt imei 356789012345678"',
};

export default function AIProductChat({ mode = "sales", onApply, onClose }) {
  const akuisisi = mode === "akuisisi";
  const [teks, setTeks] = useState("");
  const [busy, setBusy] = useState(false);
  const [riwayat, setRiwayat] = useState([]);
  const areaRef = useRef(null);

  useEffect(() => {
    // Auto-scroll ke pesan terbaru — tanpa ini balasan AI muncul di luar layar
    // dan kasir mengira panelnya tidak merespons.
    areaRef.current?.scrollTo({ top: areaRef.current.scrollHeight, behavior: "smooth" });
  }, [riwayat, busy]);

  const kirim = async () => {
    const t = teks.trim();
    if (!t || busy) return;
    setRiwayat(r => [...r, { role: "user", text: t }]);
    setTeks("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai-produk/parse", { teks: t, mode });
      setRiwayat(r => [...r, {
        role: "ai",
        items: data.items || [],
        catatan: data.catatan || "",
        provider: data.provider || "",
        terpakai: {},
      }]);
    } catch (e) {
      // 503 = AI belum dikonfigurasi. Ditampilkan tenang DI DALAM panel —
      // toast merah untuk soal konfigurasi hanya bikin kasir mengira aplikasi rusak.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Gagal menghubungi server AI";
      setRiwayat(r => [...r, { role: "ai", error: detail, tenang: status === 503 }]);
    } finally {
      setBusy(false);
    }
  };

  // Nama field item beda per mode (bentuk backend beda), diseragamkan di sini
  const nama = (it) => (akuisisi ? it.type_hp : it.nama_barang);
  const harga = (it) => (akuisisi ? it.harga_akuisisi : it.harga);

  const pakai = (mi, idxs, items) => {
    onApply(items);
    // Tandai baris yang sudah masuk supaya tidak terklik dua kali → dobel baris di form
    setRiwayat(r => r.map((m, i) => (i !== mi ? m : {
      ...m,
      terpakai: { ...(m.terpakai || {}), ...Object.fromEntries(idxs.map(x => [x, true])) },
    })));
  };

  return (
    <div
      className="fixed right-4 bottom-4 z-[60] w-[min(94vw,400px)] max-h-[72vh] flex flex-col bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden"
      data-testid="ai-product-chat">
      {/* Header — warna mengikuti modal induk (emerald=sales, violet=akuisisi) */}
      <div className={`${akuisisi ? "bg-gradient-to-r from-violet-600 to-fuchsia-600" : "bg-gradient-to-r from-emerald-600 to-teal-600"} text-white px-4 py-3 flex items-center justify-between shrink-0`}>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <div>
            <p className="text-sm font-semibold leading-tight">Input Produk via AI</p>
            <p className="text-[10px] opacity-80">
              {akuisisi ? "Nota Akuisisi" : "Nota Penjualan"} · tempel teks bebas, AI yang mengurai
            </p>
          </div>
        </div>
        <button onClick={onClose} data-testid="btn-ai-chat-close" className="p-1.5 hover:bg-white/20 rounded-lg">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Riwayat chat */}
      <div ref={areaRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50 min-h-[140px]">
        {riwayat.length === 0 && !busy && (
          <div className="text-xs text-slate-500 bg-white border border-dashed border-slate-300 rounded-lg p-3">
            <p className="font-medium text-slate-600 mb-1">Tempel daftar barang dalam teks bebas.</p>
            <p>{CONTOH[mode] || CONTOH.sales}</p>
          </div>
        )}

        {riwayat.map((m, mi) => m.role === "user" ? (
          <div key={mi} className="ml-8 bg-slate-800 text-white text-xs rounded-xl rounded-br-sm px-3 py-2 whitespace-pre-wrap">
            {m.text}
          </div>
        ) : m.error ? (
          <div key={mi} data-testid="ai-chat-error"
            className={`mr-6 text-xs rounded-xl rounded-bl-sm px-3 py-2 border ${m.tenang ? "bg-slate-100 border-slate-200 text-slate-600" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
            <p className="flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {m.error}</p>
          </div>
        ) : (
          <div key={mi} className="mr-6 bg-white border border-slate-200 rounded-xl rounded-bl-sm p-2.5 space-y-1.5">
            {(m.items || []).length === 0 ? (
              <p className="text-xs text-slate-500">Tidak ada barang yang bisa diurai.</p>
            ) : (m.items || []).map((it, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-900 truncate">
                    {nama(it) || "-"}
                    {!akuisisi && it.qty > 1 && <span className="ml-1 text-[10px] text-slate-500">× {it.qty}</span>}
                    {!akuisisi && <span className="ml-1 px-1 rounded bg-slate-200 text-slate-600 text-[9px] font-bold">{it.kode}</span>}
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    {idr(harga(it) || 0)}{it.imei ? ` · IMEI ${it.imei}` : ""}
                  </p>
                </div>
                <button onClick={() => pakai(mi, [i], [it])} disabled={!!m.terpakai?.[i]}
                  data-testid={`btn-ai-apply-${mi}-${i}`}
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50">
                  {m.terpakai?.[i] ? <CheckCheck className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                  {m.terpakai?.[i] ? "Masuk" : "Masukkan ke Form"}
                </button>
              </div>
            ))}
            {(m.items || []).length > 1 && (
              <button onClick={() => pakai(mi, m.items.map((_, i) => i), m.items)}
                data-testid={`btn-ai-apply-all-${mi}`}
                className="w-full text-[11px] font-medium py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
                Masukkan Semua ({m.items.length})
              </button>
            )}
            {m.catatan && (
              <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                {m.catatan}
              </p>
            )}
            {m.provider && <p className="text-[9px] text-slate-400 text-right">via {m.provider}</p>}
          </div>
        ))}

        {busy && (
          <div className="mr-6 inline-flex items-center gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-xl px-3 py-2"
            data-testid="ai-chat-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI sedang mengurai…
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-2.5 flex items-end gap-2 shrink-0 bg-white">
        <textarea value={teks} onChange={e => setTeks(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); kirim(); } }}
          rows={2} placeholder="Tempel teks barang di sini… (Enter untuk kirim)"
          data-testid="ai-chat-input"
          className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-xs resize-none" />
        <button onClick={kirim} disabled={busy || !teks.trim()} data-testid="btn-ai-chat-kirim"
          className={`h-9 px-3 rounded-lg text-white disabled:opacity-40 ${akuisisi ? "bg-violet-600 hover:bg-violet-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
